import React, { useEffect, useMemo } from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { observer } from "mobx-react";

import AuthStore from "stores/AuthStore";
import ProfileStore from "stores/ProfileStore";

import SignIn from "pages/SignIn";
import ForgotPassword from "pages/ForgotPassword";
import WorkoutMain from "pages/WorkoutMain/WorkoutMain";
import WorkoutFamilyDetails from "pages/WorkoutMain/WorkoutFamilyDetails/WorkoutFamilyDetails";
import WorkoutCategoryDetails from "pages/WorkoutMain/WorkoutCategoryDetails";
import Workouts from "pages/WorkoutMain/Workouts";
import WorkoutDetails from "pages/Workout/WorkoutDetails";
import Calendar from "pages/Calendar";
import Today from "pages/Today/Today";
import OnboardingDisabledMessage from "pages/OnboardingDisabledMessage/OnboardingDisabledMessage";
import WorkoutMovement from "pages/Workout/WorkoutMovement";

import GlobalNavigationContext from "./context";
import ROUTES from "./routes";

const PrivateRoute = () => {
  const location = useLocation();

  if (AuthStore.isAuthorized) {
    return <Outlet />;
  }

  return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
};

const AppRouter: React.FC = observer(() => {
  const navigate = useNavigate();

  useEffect(() => {
    AuthStore.init();

    if (AuthStore.isAuthorized) {
      ProfileStore.fetch();
    }
  }, [navigate]);

  const redirectIfNotAccess = AuthStore.isAuthorized
    ? ROUTES.TODAY
    : ROUTES.SIGN_IN;

  const navigationContext = useMemo(
    () => ({
      navigateToLogin: () => navigate(ROUTES.SIGN_IN),
    }),
    [navigate],
  );

  return (
    <GlobalNavigationContext.Provider value={navigationContext}>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={redirectIfNotAccess} replace />}
        />
        <Route path={ROUTES.SIGN_IN} element={<SignIn />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />

        <Route element={<PrivateRoute />}>
          <Route path={ROUTES.TODAY} element={<Today />} />
          <Route path={ROUTES.WORKOUT} element={<WorkoutMain />} />
          <Route
            path={ROUTES.WORKOUT_FAMILY_DETAILS}
            element={<WorkoutFamilyDetails />}
          />
          <Route
            path={ROUTES.WORKOUT_CATEGORY_DETAILS}
            element={<WorkoutCategoryDetails />}
          />

          <Route
            path={ROUTES.ONBOARDINGDISABLEDMESSAGE}
            element={<OnboardingDisabledMessage />}
          />

          {[
            ROUTES.WORKOUT_FAVORITES,
            ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUTS,
          ].map(p => (
            <Route key={p} path={p} element={<Workouts />} />
          ))}

          {[
            ROUTES.TODAY_WORKOUT_DETAILS,
            ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUT_DETAILS,
            ROUTES.WORKOUT_FAVORITES_WORKOUT_DETAILS,
            ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUTS_WORKOUT_DETAILS,
            ROUTES.WORKOUT_SEARCH_WORKOUT_DETAILS,
            ROUTES.CALENDAR_WORKOUT_SCHEDULE_DATE_DETAILS,
          ].map(p => (
            <Route key={p} path={p} element={<WorkoutDetails />} />
          ))}

          {[
            ROUTES.TODAY_WORKOUT_DETAILS_MOVEMENT_DETAILS,
            ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUT_DETAILS_MOVEMENT_DETAILS,
            ROUTES.WORKOUT_FAVORITES_WORKOUT_DETAILS_MOVEMENT_DETAILS,
            ROUTES.WORKOUT_CATEGORY_DETAILS_WORKOUTS_WORKOUT_DETAILS_MOVEMENT_DETAILS,
            ROUTES.WORKOUT_SEARCH_WORKOUT_DETAILS_MOVEMENT_DETAILS,
            ROUTES.CALENDAR_WORKOUT_SCHEDULE_DATE_DETAILS_MOVEMENT_DETAILS,
          ].map(p => (
            <Route key={p} path={p} element={<WorkoutMovement />} />
          ))}

          <Route path={ROUTES.CALENDAR} element={<Calendar />} />
        </Route>

        <Route
          path="*"
          element={<Navigate to={redirectIfNotAccess} replace />}
        />
      </Routes>
    </GlobalNavigationContext.Provider>
  );
});

export default AppRouter;
